import React from "react";
import {
  calculateInvestedAmountOnly,
  calculateLumpsum,
  calculateMonthlySip,
  formatCurrency,
  generateYearlyBreakup,
  getYearPoints,
  type SipCalculatorResult,
  type SipInvestmentType,
} from "./SipCalculatorCalculations";
import type { SipCalculatorProps, ResultView, SliderKind } from "./SipCalculatorTypes";
import {
  DEFAULT_SIP_CALCULATOR_LABELS,
  DEFAULT_SIP_CALCULATOR_SYMBOLS,
  INVESTMENT_SLIDER_MARKERS,
  INVESTMENT_TYPE_TABS,
  INTEREST_RATE_SLIDER_MARKERS,
  RESULT_VIEW_TABS,
  TENURE_SLIDER_MARKERS,
  resolveSliderInputAffix,
  resolveTenureErrorSuffix,
} from "./SipCalculatorConstants";
import {
  parseValue,
  clampValue,
  normalizeInitialNumericValue,
  resolveSliderMarkers,
  buildGrowthPercentage,
  buildYearSequence,
  hasEffectiveTenureValue as checkEffectiveTenureValue,
  hasValidCalculatorInputs,
  isFieldInputValid,
  isTenureEmptyOrZero,
  resolveCalculatorFieldValue,
  resolveSummaryGrowthYears,
  restrictToMaxDigits,
  formatIndianNumber,
} from "./SipCalculatorHelpers";
import { SliderField } from "./SliderField";
import { DonutBreakdownChart } from "./DonutBreakdownChart";
import { MonthlySipAreaChart } from "./MonthlySipAreaChart";

export type { SipCalculatorProps, SipCalculatorPerTypeProps } from "./SipCalculatorTypes";
export type { SipCalculatorResult, SipInvestmentType } from "./SipCalculatorCalculations";

const EMPTY_RESULT: SipCalculatorResult = {
  futureValue: 0,
  investedAmount: 0,
  estimatedReturns: 0,
};

// Helper: Field validity update config
interface FieldValidityUpdate {
  isValid: boolean;
  currentValue: number;
  ref: React.MutableRefObject<number | null>;
}

// Helper: Update validity refs for all fields
const updateValidityRefs = (updates: FieldValidityUpdate[]) => {
  updates.forEach(({ isValid, currentValue, ref }) => {
    if (isValid) ref.current = currentValue;
  });
};

// Helper: Resolve slider value with fallback to last valid
const resolveSliderValue = (
  isValid: boolean,
  currentValue: number,
  lastValidRef: React.MutableRefObject<number | null>,
  minValue: number,
  specialCondition?: boolean
): number => {
  if (isValid) return currentValue;
  if (specialCondition) return minValue;
  return lastValidRef.current ?? minValue;
};

// Helper: Validate field and return error message
const validateFieldError = (
  parsedValue: number,
  minValue: number,
  maxValue: number,
  formatErrorSliderValue: (kind: SliderKind, value: number) => string,
  kind: SliderKind
): string | undefined => {
  if (parsedValue < minValue) {
    return `Minimum allowed value is ${formatErrorSliderValue(kind, minValue)}`;
  }
  if (parsedValue > maxValue) {
    return `Maximum allowed value is ${formatErrorSliderValue(kind, maxValue)}`;
  }
  return undefined;
};

// Helper: Compute all display flags
const computeDisplayFlags = (
  isLumpsum: boolean,
  resolvedInvestment: number | null,
  resolvedTenureYears: number | null,
  resolvedInterestRate: number | null,
  hasInterestRateValue: boolean,
  parsedInterestRate: number
) => {
  const shouldDisplayInvestedAmount = isLumpsum
    ? resolvedInvestment !== null
    : resolvedInvestment !== null && resolvedTenureYears !== null;

  const shouldDisplayReturns =
    resolvedInvestment !== null && resolvedTenureYears !== null && resolvedInterestRate !== null;

  const shouldDisplayLumpsumPartial =
    isLumpsum && resolvedInvestment !== null && !shouldDisplayReturns;

  // Used internally to compute shouldDisplaySipBasis
  const shouldDisplayYearlyBreakup = resolvedInvestment !== null && resolvedTenureYears !== null;

  const shouldDisplaySipBasis = !isLumpsum && shouldDisplayYearlyBreakup;

  const isSipRateEmptyOrZero = !isLumpsum && (!hasInterestRateValue || parsedInterestRate === 0);

  const shouldDisplaySipSummaryReturns = shouldDisplayReturns && !isSipRateEmptyOrZero;

  const shouldDisplayPartialSummary = shouldDisplayLumpsumPartial;

  return {
    shouldDisplayInvestedAmount,
    shouldDisplayReturns,
    shouldDisplayLumpsumPartial,
    shouldDisplaySipBasis,
    isSipRateEmptyOrZero,
    shouldDisplaySipSummaryReturns,
    shouldDisplayPartialSummary,
  };
};

const resolveTypeBounds = (
  type: SipInvestmentType,
  perTypeConfig: SipCalculatorProps["perTypeConfig"],
  base: Required<
    Pick<
      SipCalculatorProps,
      | "initialInvestment"
      | "initialTenureYears"
      | "initialInterestRate"
      | "investmentMin"
      | "investmentMax"
      | "investmentStep"
      | "investmentSliderMarkers"
      | "tenureMin"
      | "tenureMax"
      | "tenureStep"
      | "tenureSliderMarkers"
      | "interestRateMin"
      | "interestRateMax"
      | "interestRateSliderMarkers"
    >
  >
) => {
  const typeConfig = perTypeConfig?.[type];

  return {
    initialInvestment: typeConfig?.initialInvestment ?? base.initialInvestment,
    initialTenureYears: typeConfig?.initialTenureYears ?? base.initialTenureYears,
    initialInterestRate: typeConfig?.initialInterestRate ?? base.initialInterestRate,
    investmentMin: typeConfig?.investmentMin ?? base.investmentMin,
    investmentMax: typeConfig?.investmentMax ?? base.investmentMax,
    investmentStep: typeConfig?.investmentStep ?? base.investmentStep,
    investmentSliderMarkers: typeConfig?.investmentSliderMarkers ?? base.investmentSliderMarkers,
    tenureMin: typeConfig?.tenureMin ?? base.tenureMin,
    tenureMax: typeConfig?.tenureMax ?? base.tenureMax,
    tenureStep: typeConfig?.tenureStep ?? base.tenureStep,
    tenureSliderMarkers: typeConfig?.tenureSliderMarkers ?? base.tenureSliderMarkers,
    interestRateMin: typeConfig?.interestRateMin ?? base.interestRateMin,
    interestRateMax: typeConfig?.interestRateMax ?? base.interestRateMax,
    interestRateStep: typeConfig?.interestRateStep ?? 1,
    interestRateSliderMarkers:
      typeConfig?.interestRateSliderMarkers ?? base.interestRateSliderMarkers,
  };
};

export const SipCalculator: React.FC<SipCalculatorProps> = ({
  title = "Calculate your SIP value",
  description,
  labels,
  symbols,
  defaultInvestmentType = "sip",
  initialInvestment = 25000,
  initialTenureYears = 10,
  initialInterestRate = 12,
  investmentMin = 100,
  investmentMax = 100000000,
  investmentStep = 100,
  investmentSliderMarkers = INVESTMENT_SLIDER_MARKERS,
  tenureMin = 1,
  tenureMax = 40,
  tenureStep = 1,
  tenureSliderMarkers = TENURE_SLIDER_MARKERS,
  interestRateMin = 1,
  interestRateMax = 30,
  interestRateSliderMarkers = INTEREST_RATE_SLIDER_MARKERS,
  perTypeConfig,
  className = "",
  ...props
}) => {
  const baseBounds = React.useMemo(
    () => ({
      initialInvestment,
      initialTenureYears,
      initialInterestRate,
      investmentMin,
      investmentMax,
      investmentStep,
      investmentSliderMarkers: [...investmentSliderMarkers],
      tenureMin,
      tenureMax,
      tenureStep,
      tenureSliderMarkers: [...tenureSliderMarkers],
      interestRateMin,
      interestRateMax,
      interestRateSliderMarkers: [...interestRateSliderMarkers],
    }),
    [
      initialInvestment,
      initialTenureYears,
      initialInterestRate,
      investmentMin,
      investmentMax,
      investmentStep,
      investmentSliderMarkers,
      tenureMin,
      tenureMax,
      tenureStep,
      tenureSliderMarkers,
      interestRateMin,
      interestRateMax,
      interestRateSliderMarkers,
    ]
  );

  const [investmentType, setInvestmentType] =
    React.useState<SipInvestmentType>(defaultInvestmentType);

  const activeBounds = React.useMemo(
    () => resolveTypeBounds(investmentType, perTypeConfig, baseBounds),
    [investmentType, perTypeConfig, baseBounds]
  );

  const mergedLabels = React.useMemo(
    () => ({
      ...DEFAULT_SIP_CALCULATOR_LABELS,
      ...labels,
      ...perTypeConfig?.[investmentType]?.labels,
    }),
    [labels, perTypeConfig, investmentType]
  );

  const mergedSymbols = React.useMemo(
    () => ({
      ...DEFAULT_SIP_CALCULATOR_SYMBOLS,
      ...symbols,
    }),
    [symbols]
  );

  const currentYear = new Date().getFullYear();
  const [activeResultView, setActiveResultView] = React.useState<ResultView>("chart");
  const [showAllRows, setShowAllRows] = React.useState(false);
  const [investmentInput, setInvestmentInput] = React.useState(() => {
    const bounds = resolveTypeBounds(defaultInvestmentType, perTypeConfig, {
      initialInvestment,
      initialTenureYears,
      initialInterestRate,
      investmentMin,
      investmentMax,
      investmentStep,
      investmentSliderMarkers: [...investmentSliderMarkers],
      tenureMin,
      tenureMax,
      tenureStep,
      tenureSliderMarkers: [...tenureSliderMarkers],
      interestRateMin,
      interestRateMax,
      interestRateSliderMarkers: [...interestRateSliderMarkers],
    });

    return normalizeInitialNumericValue(
      bounds.initialInvestment,
      bounds.investmentMin,
      bounds.investmentMax
    );
  });
  const [tenureInput, setTenureInput] = React.useState(() => {
    const bounds = resolveTypeBounds(defaultInvestmentType, perTypeConfig, {
      initialInvestment,
      initialTenureYears,
      initialInterestRate,
      investmentMin,
      investmentMax,
      investmentStep,
      investmentSliderMarkers: [...investmentSliderMarkers],
      tenureMin,
      tenureMax,
      tenureStep,
      tenureSliderMarkers: [...tenureSliderMarkers],
      interestRateMin,
      interestRateMax,
      interestRateSliderMarkers: [...interestRateSliderMarkers],
    });

    return normalizeInitialNumericValue(
      bounds.initialTenureYears,
      bounds.tenureMin,
      bounds.tenureMax
    );
  });
  const [interestRateInput, setInterestRateInput] = React.useState(() => {
    const bounds = resolveTypeBounds(defaultInvestmentType, perTypeConfig, {
      initialInvestment,
      initialTenureYears,
      initialInterestRate,
      investmentMin,
      investmentMax,
      investmentStep,
      investmentSliderMarkers: [...investmentSliderMarkers],
      tenureMin,
      tenureMax,
      tenureStep,
      tenureSliderMarkers: [...tenureSliderMarkers],
      interestRateMin,
      interestRateMax,
      interestRateSliderMarkers: [...interestRateSliderMarkers],
    });

    return normalizeInitialNumericValue(
      bounds.initialInterestRate,
      bounds.interestRateMin,
      bounds.interestRateMax
    );
  });

  const touchedFieldsRef = React.useRef({
    investment: false,
    tenure: false,
    interestRate: false,
  });
  const lastChartYearPointsRef = React.useRef<number[]>([]);
  const lastValidInvestmentRef = React.useRef<number | null>(null);
  const lastValidTenureRef = React.useRef<number | null>(null);
  const lastValidInterestRateRef = React.useRef<number | null>(null);

  /** Untouched fields get the target tab's AEM defaults; user-edited fields stay shared. */
  const handleInvestmentTypeChange = React.useCallback(
    (type: SipInvestmentType) => {
      const bounds = resolveTypeBounds(type, perTypeConfig, baseBounds);
      const touched = touchedFieldsRef.current;

      if (!touched.investment) {
        setInvestmentInput(
          normalizeInitialNumericValue(
            bounds.initialInvestment,
            bounds.investmentMin,
            bounds.investmentMax
          )
        );
      }
      if (!touched.tenure) {
        setTenureInput(
          normalizeInitialNumericValue(
            bounds.initialTenureYears,
            bounds.tenureMin,
            bounds.tenureMax
          )
        );
      }
      if (!touched.interestRate) {
        setInterestRateInput(
          normalizeInitialNumericValue(
            bounds.initialInterestRate,
            bounds.interestRateMin,
            bounds.interestRateMax
          )
        );
      }

      setInvestmentType(type);
      setShowAllRows(false);
    },
    [baseBounds, perTypeConfig]
  );

  const parsedInvestment = parseValue(investmentInput);
  const parsedTenure = parseValue(tenureInput);
  const parsedInterestRate = parseValue(interestRateInput);

  const {
    investmentMin: activeInvestmentMin,
    investmentMax: activeInvestmentMax,
    investmentStep: activeInvestmentStep,
    investmentSliderMarkers: activeInvestmentSliderMarkers,
    tenureMin: activeTenureMin,
    tenureMax: activeTenureMax,
    tenureStep: activeTenureStep,
    tenureSliderMarkers: activeTenureSliderMarkers,
    interestRateMin: activeInterestRateMin,
    interestRateMax: activeInterestRateMax,
    interestRateSliderMarkers: activeInterestRateSliderMarkers,
  } = activeBounds;

  const hasInvestmentValue = investmentInput.trim() !== "";
  const hasEffectiveTenureValue = checkEffectiveTenureValue(tenureInput);
  const hasInterestRateValue = interestRateInput.trim() !== "";

  const isInvestmentValid = isFieldInputValid(
    investmentInput,
    activeInvestmentMin,
    activeInvestmentMax
  );
  const isTenureValid = isFieldInputValid(tenureInput, activeTenureMin, activeTenureMax);
  const isInterestRateValid = isFieldInputValid(
    interestRateInput,
    activeInterestRateMin,
    activeInterestRateMax
  );

  const canCalculate = hasValidCalculatorInputs(investmentInput, tenureInput, interestRateInput, {
    investmentMin: activeInvestmentMin,
    investmentMax: activeInvestmentMax,
    tenureMin: activeTenureMin,
    tenureMax: activeTenureMax,
    interestRateMin: activeInterestRateMin,
    interestRateMax: activeInterestRateMax,
  });

  const currentInvestment = clampValue(parsedInvestment, activeInvestmentMin, activeInvestmentMax);
  const currentTenureYears = clampValue(parsedTenure, activeTenureMin, activeTenureMax);
  const currentInterestRate = clampValue(
    Math.round(parsedInterestRate),
    activeInterestRateMin,
    activeInterestRateMax
  );

  // Update refs for valid fields
  updateValidityRefs([
    { isValid: isInvestmentValid, currentValue: currentInvestment, ref: lastValidInvestmentRef },
    { isValid: isTenureValid, currentValue: currentTenureYears, ref: lastValidTenureRef },
    {
      isValid: isInterestRateValid,
      currentValue: currentInterestRate,
      ref: lastValidInterestRateRef,
    },
  ]);

  const resolvedInvestment = resolveCalculatorFieldValue(
    hasInvestmentValue,
    isInvestmentValid,
    currentInvestment,
    lastValidInvestmentRef.current ?? undefined
  );
  const resolvedTenureYears = resolveCalculatorFieldValue(
    hasEffectiveTenureValue,
    isTenureValid,
    currentTenureYears,
    lastValidTenureRef.current ?? undefined
  );
  const resolvedInterestRate = resolveCalculatorFieldValue(
    hasInterestRateValue,
    isInterestRateValid,
    currentInterestRate,
    lastValidInterestRateRef.current ?? undefined
  );

  const sliderInvestment = resolveSliderValue(
    isInvestmentValid,
    currentInvestment,
    lastValidInvestmentRef,
    activeInvestmentMin
  );

  // Extract tenure condition for clarity - avoid nested ternary
  const isTenureInputEmpty = isTenureEmptyOrZero(tenureInput);
  const sliderTenureYears = resolveSliderValue(
    isTenureValid,
    currentTenureYears,
    lastValidTenureRef,
    activeTenureMin,
    isTenureInputEmpty
  );

  const sliderInterestRate = resolveSliderValue(
    isInterestRateValid,
    currentInterestRate,
    lastValidInterestRateRef,
    activeInterestRateMin
  );

  const isLumpsum = investmentType === "lumpsum";

  const {
    shouldDisplayInvestedAmount,
    shouldDisplayReturns,
    shouldDisplayLumpsumPartial,
    shouldDisplaySipBasis,
    isSipRateEmptyOrZero,
    shouldDisplaySipSummaryReturns,
    shouldDisplayPartialSummary,
  } = computeDisplayFlags(
    isLumpsum,
    resolvedInvestment,
    resolvedTenureYears,
    resolvedInterestRate,
    hasInterestRateValue,
    parsedInterestRate
  );

  const breakupInterestRate =
    shouldDisplayReturns && !isSipRateEmptyOrZero ? resolvedInterestRate! : 0;

  const investment = canCalculate ? currentInvestment : 0;
  const tenureYears = canCalculate ? currentTenureYears : 0;
  const interestRate = canCalculate ? currentInterestRate : 0;
  const resultTenureYears = resolvedTenureYears ?? 0;
  const summaryGrowthYears = shouldDisplayReturns
    ? resultTenureYears
    : resolveSummaryGrowthYears(
        hasEffectiveTenureValue,
        isTenureValid,
        parsedTenure,
        activeTenureMin,
        activeTenureMax,
        lastValidTenureRef.current
      );

  const formatErrorSliderValue = (kind: SliderKind, value: number): string => {
    const rounded = String(Math.round(value));
    const displayValue = kind === "currency" ? formatIndianNumber(rounded) : rounded;
    const affix = resolveSliderInputAffix(kind, mergedSymbols);
    if (affix.prefix) return `${affix.prefix}${displayValue}`;
    if (kind === "years") {
      const suffix = resolveTenureErrorSuffix(value, affix.suffix ?? "");
      return `${displayValue} ${suffix}`.trimEnd();
    }
    return `${displayValue}${affix.suffix ?? ""}`;
  };

  const investmentError = validateFieldError(
    parsedInvestment,
    activeInvestmentMin,
    activeInvestmentMax,
    formatErrorSliderValue,
    "currency"
  );

  const tenureError = validateFieldError(
    parsedTenure,
    activeTenureMin,
    activeTenureMax,
    formatErrorSliderValue,
    "years"
  );

  const interestRateError = validateFieldError(
    parsedInterestRate,
    activeInterestRateMin,
    activeInterestRateMax,
    formatErrorSliderValue,
    "percent"
  );

  const investedAmount = React.useMemo(() => {
    if (!shouldDisplayInvestedAmount) return 0;

    if (isLumpsum) {
      return calculateInvestedAmountOnly("lumpsum", resolvedInvestment!, 0);
    }

    return calculateInvestedAmountOnly(investmentType, resolvedInvestment!, resolvedTenureYears!);
  }, [
    investmentType,
    isLumpsum,
    resolvedInvestment,
    resolvedTenureYears,
    shouldDisplayInvestedAmount,
  ]);

  const result = React.useMemo(() => {
    if (shouldDisplayLumpsumPartial) {
      return {
        futureValue: investedAmount,
        investedAmount,
        estimatedReturns: 0,
      };
    }

    if (!shouldDisplaySipSummaryReturns) {
      return { ...EMPTY_RESULT, investedAmount };
    }

    return investmentType === "sip"
      ? calculateMonthlySip(resolvedInvestment!, resolvedInterestRate!, resolvedTenureYears!)
      : calculateLumpsum(resolvedInvestment!, resolvedInterestRate!, resolvedTenureYears!);
  }, [
    investedAmount,
    investmentType,
    resolvedInvestment,
    resolvedInterestRate,
    resolvedTenureYears,
    shouldDisplayLumpsumPartial,
    shouldDisplaySipSummaryReturns,
  ]);

  const chartYearPoints = React.useMemo(() => {
    if (resolvedTenureYears === null) {
      return getYearPoints(0);
    }

    const nextPoints = getYearPoints(resultTenureYears);

    if (canCalculate) {
      lastChartYearPointsRef.current = nextPoints;
      return nextPoints;
    }

    return lastChartYearPointsRef.current.length > 0 ? lastChartYearPointsRef.current : nextPoints;
  }, [canCalculate, resolvedTenureYears, resultTenureYears]);

  const yearlyBreakup = React.useMemo(() => {
    if (!shouldDisplaySipBasis) {
      return generateYearlyBreakup(investmentType, 0, 0, chartYearPoints);
    }

    return generateYearlyBreakup(
      investmentType,
      resolvedInvestment!,
      breakupInterestRate,
      chartYearPoints
    );
  }, [
    breakupInterestRate,
    chartYearPoints,
    investmentType,
    resolvedInvestment,
    shouldDisplaySipBasis,
  ]);

  const chartData = React.useMemo(
    () =>
      yearlyBreakup.map((row) => ({
        label: row.year,
        primary: row.investedAmount,
        secondary: row.interestEarned,
      })),
    [yearlyBreakup]
  );

  const tableRows = React.useMemo(() => {
    if (!hasEffectiveTenureValue) {
      return [];
    }

    const tableTenureYears = shouldDisplayReturns
      ? resultTenureYears
      : resolveSummaryGrowthYears(
          hasEffectiveTenureValue,
          isTenureValid,
          parsedTenure,
          activeTenureMin,
          activeTenureMax,
          lastValidTenureRef.current
        );
    const yearSequence = buildYearSequence(tableTenureYears);
    const tableAmount = resolvedInvestment ?? 0;
    const tableRate = resolvedInvestment !== null ? breakupInterestRate : 0;

    return generateYearlyBreakup(investmentType, tableAmount, tableRate, yearSequence);
  }, [
    activeTenureMax,
    activeTenureMin,
    breakupInterestRate,
    hasEffectiveTenureValue,
    investmentType,
    isTenureValid,
    parsedTenure,
    resolvedInvestment,
    resultTenureYears,
    shouldDisplayReturns,
  ]);

  const growthPercentage = React.useMemo(
    () => buildGrowthPercentage(result, mergedSymbols.percentSymbol),
    [result, mergedSymbols.percentSymbol]
  );

  const investmentMarkerValues = React.useMemo(
    () =>
      resolveSliderMarkers(activeInvestmentSliderMarkers, activeInvestmentMin, activeInvestmentMax),
    [activeInvestmentSliderMarkers, activeInvestmentMin, activeInvestmentMax]
  );
  const tenureMarkerValues = React.useMemo(
    () => resolveSliderMarkers(activeTenureSliderMarkers, activeTenureMin, activeTenureMax),
    [activeTenureSliderMarkers, activeTenureMin, activeTenureMax]
  );
  const interestRateMarkerValues = React.useMemo(
    () =>
      resolveSliderMarkers(
        activeInterestRateSliderMarkers,
        activeInterestRateMin,
        activeInterestRateMax
      ),
    [activeInterestRateSliderMarkers, activeInterestRateMax, activeInterestRateMin]
  );

  const prevDefaultInvestmentType = React.useRef(defaultInvestmentType);

  React.useEffect(() => {
    if (prevDefaultInvestmentType.current === defaultInvestmentType) return;

    prevDefaultInvestmentType.current = defaultInvestmentType;
    setInvestmentType(defaultInvestmentType);
  }, [defaultInvestmentType]);

  React.useEffect(() => {
    if (perTypeConfig) return;

    setInvestmentInput(
      normalizeInitialNumericValue(initialInvestment, activeInvestmentMin, activeInvestmentMax)
    );
  }, [initialInvestment, activeInvestmentMax, activeInvestmentMin, perTypeConfig]);

  React.useEffect(() => {
    if (perTypeConfig) return;

    setTenureInput(
      normalizeInitialNumericValue(initialTenureYears, activeTenureMin, activeTenureMax)
    );
  }, [initialTenureYears, activeTenureMax, activeTenureMin, perTypeConfig]);

  React.useEffect(() => {
    if (perTypeConfig) return;

    setInterestRateInput(
      normalizeInitialNumericValue(
        initialInterestRate,
        activeInterestRateMin,
        activeInterestRateMax
      )
    );
  }, [initialInterestRate, activeInterestRateMax, activeInterestRateMin, perTypeConfig]);

  React.useEffect(() => {
    setShowAllRows(false);
  }, [investmentType, investment, tenureYears, interestRate]);

  /* --- Shared sub-renders --- */

  const renderInvestmentTypeChips = () => (
    <div className="flex flex-col gap-3 p-[8px] md:p-[20px]">
      <p className="font-rubik text-black-normal text-[14px] leading-[18px] font-medium md:text-[16px]">
        {mergedLabels.investmentType}
      </p>
      <div className="flex gap-2">
        {INVESTMENT_TYPE_TABS.map((tab) => {
          const isActive = tab.id === investmentType;
          const tabLabel =
            tab.id === "sip" ? mergedLabels.investmentTypeSip : mergedLabels.investmentTypeLumpsum;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => handleInvestmentTypeChange(tab.id)}
              className="font-rubik inline-flex h-[36px] min-w-[40px] cursor-pointer items-center justify-center rounded-[8px] border border-solid border-[#002953] p-[6px] text-[12px] font-normal transition-colors md:p-[8px] md:text-[12px]"
              style={
                isActive
                  ? { backgroundColor: "#002953", borderColor: "#002953", color: "white" }
                  : { backgroundColor: "white", borderColor: "#002953", color: "#002953" }
              }
            >
              {tabLabel}
            </button>
          );
        })}
      </div>
    </div>
  );

  const resolveInvestmentLabel = (isCompactLayout: boolean) => {
    if (isCompactLayout) {
      return mergedLabels.investmentAmount;
    }

    return investmentType === "sip"
      ? mergedLabels.monthlyInvestment
      : mergedLabels.lumpsumInvestment;
  };

  const renderSliders = (isCompactLayout = false) => (
    <div className="flex flex-col">
      <SliderField
        label={resolveInvestmentLabel(isCompactLayout)}
        kind="currency"
        value={sliderInvestment}
        inputValue={investmentInput}
        min={activeInvestmentMin}
        max={activeInvestmentMax}
        step={activeInvestmentStep}
        error={investmentError}
        markerValues={investmentMarkerValues}
        symbols={mergedSymbols}
        mobileFocusHighlight={isCompactLayout}
        onInputChange={(v) => {
          touchedFieldsRef.current.investment = true;
          // Restrict to 9 digits max (allows values up to 99,99,99,999 which covers 10Cr max)
          const maxDigits = String(activeInvestmentMax).length;
          const restrictedValue = restrictToMaxDigits(v, maxDigits);
          setInvestmentInput(restrictedValue);
        }}
      />
      <SliderField
        label={mergedLabels.tenureYears}
        kind="years"
        value={sliderTenureYears}
        inputValue={tenureInput}
        min={activeTenureMin}
        max={activeTenureMax}
        step={activeTenureStep}
        error={tenureError}
        markerValues={tenureMarkerValues}
        symbols={mergedSymbols}
        mobileFocusHighlight={isCompactLayout}
        onInputChange={(v) => {
          touchedFieldsRef.current.tenure = true;
          // Restrict to max digits based on activeTenureMax
          const maxDigits = String(activeTenureMax).length;
          const restrictedValue = restrictToMaxDigits(v, maxDigits);
          setTenureInput(restrictedValue);
        }}
      />
      <SliderField
        label={mergedLabels.expectedReturn}
        kind="percent"
        value={sliderInterestRate}
        inputValue={interestRateInput}
        min={activeInterestRateMin}
        max={activeInterestRateMax}
        step={1}
        error={interestRateError}
        markerValues={interestRateMarkerValues}
        symbols={mergedSymbols}
        mobileFocusHighlight={isCompactLayout}
        onInputChange={(v) => {
          touchedFieldsRef.current.interestRate = true;
          // Restrict to max digits based on activeInterestRateMax
          const maxDigits = String(activeInterestRateMax).length;
          const restrictedValue = restrictToMaxDigits(v, maxDigits);
          setInterestRateInput(restrictedValue);
        }}
      />
    </div>
  );

  const renderResultTabs = () => (
    <div className="border-grey-cloud border-b">
      <div className="flex min-h-8 items-center gap-[16px]">
        {RESULT_VIEW_TABS.map((tab) => {
          const isActive = tab.id === activeResultView;
          const tabLabel = tab.id === "chart" ? mergedLabels.chartTab : mergedLabels.tableTab;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveResultView(tab.id)}
              className={`font-rubik relative cursor-pointer pb-3 text-[12px] transition-colors md:text-[16px] ${
                isActive ? "font-medium text-[#FF6700]" : "font-normal text-[#002953]"
              }`}
            >
              {tabLabel}
              {isActive ? (
                <span className="absolute inset-x-0 bottom-0 h-[2px] rounded-tl-[2px] rounded-tr-[2px] bg-[#FF6700]" />
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderChartContent = () => {
    if (investmentType === "lumpsum") {
      return (
        <>
          <h3 className="text-black-normal font-rubik mb-[9px] text-[14px] font-medium md:text-[16px]">
            {mergedLabels.chartTitle}
          </h3>
          <DonutBreakdownChart
            investedAmount={result.investedAmount}
            estimatedReturns={result.estimatedReturns}
            futureValue={result.futureValue}
            rupeeSymbol={mergedSymbols.rupeeSymbol}
            labels={mergedLabels}
          />
        </>
      );
    }

    return (
      <div className="bfl-sip-chart-wrapper w-full min-w-0">
        <style>{`
          .bfl-sip-chart-wrapper .bfl-chart {
            flex-direction: column !important;
            gap: 16px !important;
            width: 100% !important;
          }
          .bfl-sip-chart-wrapper .bfl-chart > div:first-child {
            width: 100% !important;
            min-width: 0 !important;
            flex: 1 1 auto !important;
          }
          .bfl-sip-chart-wrapper .bfl-chart > div:last-child {
            width: 100% !important;
            flex-direction: row !important;
            align-items: center !important;
            justify-content: flex-start !important;
            gap: 16px !important;
          }
          .bfl-sip-chart-wrapper .recharts-tooltip-cursor {
            display: none !important;
          }
          .bfl-sip-chart-wrapper .recharts-cartesian-grid-vertical line {
            stroke: #ccc !important;
            stroke-width: 1;
          }
        `}</style>
        <MonthlySipAreaChart
          title={mergedLabels.chartTitle}
          data={chartData}
          height={215}
          primaryLabel={mergedLabels.donutInvestedAmount}
          secondaryLabel={mergedLabels.donutEstimatedReturns}
          primaryColor="#7086FD"
          secondaryColor="#6FD195"
          valuePrefix={mergedSymbols.rupeeSymbol}
          xAxisFormatter={(value) => String(currentYear + Number(value))}
          className="w-full"
        />
      </div>
    );
  };

  const renderTableContent = () => {
    const visibleTableRows = showAllRows ? tableRows : tableRows.slice(0, 4);

    return (
      <div className="flex flex-col">
        <h3 className="text-black-normal font-rubik mb-[9px] text-[14px] font-medium md:text-[16px]">
          {mergedLabels.chartTitle}
        </h3>
        <div className="relative min-w-0">
          <div
            className={`min-w-0 rounded-lg border border-[#E0E0E0] ${
              showAllRows ? "overflow-auto" : "overflow-x-auto overflow-y-hidden"
            }`}
          >
            <table className="font-rubik w-full border-collapse">
              <thead className="bg-grey-cloud">
                <tr>
                  <th className="text-black-normal border-r border-[#E5E5E5] p-3 text-center text-[12px] font-medium whitespace-nowrap md:text-[14px]">
                    {mergedLabels.tableYears}
                  </th>
                  <th className="text-black-normal border-r border-[#E5E5E5] p-3 text-center text-[12px] font-medium whitespace-nowrap md:text-[14px]">
                    {mergedLabels?.tableTotalAmount}
                  </th>
                  <th className="text-black-normal border-r border-[#E5E5E5] p-3 text-center text-[12px] font-medium whitespace-nowrap md:text-[14px]">
                    {mergedLabels?.tableYearlyInvestment}
                  </th>
                  <th className="text-black-normal p-3 text-center text-[12px] font-medium whitespace-nowrap md:text-[14px]">
                    {mergedLabels.tableInterestEarned}
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleTableRows.map((row, index) => (
                  <tr
                    key={`${investmentType}-${row.year}`}
                    className={index % 2 === 0 ? "bg-white" : "bg-[#FAFAFA]"}
                  >
                    <td className="text-black-normal border-t border-r border-[#E5E5E5] p-3 text-center text-[12px] font-medium whitespace-nowrap md:text-[14px]">
                      {row.year}
                    </td>
                    <td className="text-black-normal border-t border-r border-[#E5E5E5] p-3 text-center text-[12px] whitespace-nowrap md:text-[14px]">
                      {formatCurrency(row.totalAmount, mergedSymbols.rupeeSymbol)}
                    </td>
                    <td className="text-black-normal border-t border-r border-[#E5E5E5] p-3 text-center text-[12px] whitespace-nowrap md:text-[14px]">
                      {formatCurrency(row.investedAmount, mergedSymbols.rupeeSymbol)}
                    </td>
                    <td className="text-black-normal border-t border-[#E5E5E5] p-3 text-center text-[12px] whitespace-nowrap md:text-[14px]">
                      {formatCurrency(row.interestEarned, mergedSymbols.rupeeSymbol)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!showAllRows && tableRows.length > 4 ? (
            <div className="pointer-events-none absolute bottom-0 left-0 h-16 w-full bg-gradient-to-t from-white to-transparent" />
          ) : null}
        </div>

        {tableRows.length > 4 ? (
          <div className="mt-2 flex justify-center">
            <button
              type="button"
              onClick={() => setShowAllRows((current) => !current)}
              className="font-rubik flex cursor-pointer items-center gap-1 text-[14px] font-medium text-[#FF6700]"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                className={`text-[#FF6700] transition-transform md:h-4 md:w-4 ${showAllRows ? "rotate-180" : ""}`}
              >
                <path
                  d="M2 4.5L6 8.5L10 4.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {showAllRows ? mergedLabels.tableShowLess : mergedLabels.tableShowMore}
            </button>
          </div>
        ) : null}
      </div>
    );
  };

  const renderSummaryValues = (isMobile = false) => (
    <div className="flex flex-col gap-5 pt-2 pb-[12px]">
      <div className="flex flex-col gap-[4px]">
        <p
          className={`font-rubik text-[12px] leading-none font-medium text-[#DCDEF3] ${isMobile ? "" : "sm:text-[14px]"}`}
        >
          {mergedLabels.summaryTotalValue}
        </p>
        <p
          className={`font-rubik leading-none font-semibold text-white ${isMobile ? "text-[22px]" : "text-[22px] sm:text-[28px]"}`}
        >
          {shouldDisplaySipSummaryReturns || shouldDisplayPartialSummary
            ? formatCurrency(result.futureValue, mergedSymbols.rupeeSymbol)
            : "--"}
        </p>
        <p className="font-rubik leading-none text-white">
          <span className="text-[14px] font-semibold">
            {shouldDisplaySipSummaryReturns || shouldDisplayPartialSummary
              ? growthPercentage
              : "--"}
          </span>
          {shouldDisplaySipSummaryReturns || shouldDisplayPartialSummary ? (
            <span className="text-[12px] font-normal text-[#DCDEF3]">
              {` ${mergedLabels.summaryGrowthSuffix.replace("{years}", String(summaryGrowthYears))}`}
            </span>
          ) : null}
        </p>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex min-w-0 flex-col gap-[4px]">
          <p
            className={`font-rubik leading-none font-normal text-[#DCDEF3] ${isMobile ? "text-[10px]" : "text-[10px] sm:text-[14px]"}`}
          >
            {mergedLabels.summaryEstimatedReturns}
          </p>
          <p
            className={`font-rubik leading-none font-normal text-white ${isMobile ? "text-[16px]" : "text-[16px] md:text-[24px]"}`}
          >
            {shouldDisplaySipSummaryReturns
              ? formatCurrency(result.estimatedReturns, mergedSymbols.rupeeSymbol)
              : "--"}
          </p>
        </div>

        <div className="flex min-w-0 flex-col gap-[4px]">
          <p
            className={`font-rubik leading-none font-normal text-[#DCDEF3] ${isMobile ? "text-[10px]" : "text-[10px] md:text-[14px]"}`}
          >
            {mergedLabels.summaryInvestedAmount}
          </p>
          <p
            className={`font-rubik leading-none font-normal whitespace-nowrap text-white ${isMobile ? "text-[16px]" : "text-[16px] md:text-[24px]"}`}
          >
            {shouldDisplayInvestedAmount
              ? formatCurrency(investedAmount, mergedSymbols.rupeeSymbol)
              : "--"}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={`bfl-sip-calculator font-rubik mb-3 md:px-5 md:py-2 ${className}`.trim()}
      data-cmp="SipCalculator"
      {...props}
    >
      <div className="mb-6 flex flex-col gap-2">
        <h2 className="font-rubik text-black-normal text-[20px] leading-none font-medium md:text-[20px]">
          {title}
        </h2>
        {description ? (
          <p className="font-rubik text-grey-normal text-[16px] leading-7 md:text-[18px]">
            {description}
          </p>
        ) : null}
      </div>

      {/* Mobile Layout (< 900px) */}
      <div className="min-[900px]:hidden">
        <div className="rounded-[12px] p-[8px]" style={{ backgroundColor: "#E5EAF7" }}>
          {/* Purple summary - rounded top only */}
          <div
            className="rounded-t-[12px] px-[20px] py-[16px] text-white"
            style={{ backgroundColor: "#5164C8" }}
          >
            {renderSummaryValues(true)}
          </div>

          {/* White card with inputs */}
          <div className="rounded-b-[12px] bg-white">
            {renderInvestmentTypeChips()}
            {renderSliders(true)}
          </div>

          {/* White card with chart/table */}
          <div className="mt-[12px] rounded-[12px] bg-white p-[12px]">
            {renderResultTabs()}
            <div className="pt-4">
              {activeResultView === "chart" ? renderChartContent() : renderTableContent()}
            </div>
          </div>
        </div>
      </div>

      {/* Tablet / desktop layout (≥ 900px) — stacked 900–1260px, side-by-side from 1261px */}
      <div className="hidden min-[900px]:grid min-[900px]:grid-cols-1 min-[900px]:items-start min-[900px]:gap-[16px] min-[1261px]:grid-cols-2">
        {/* Left card - Inputs */}
        <div
          className="bfl-sip-calc-desktop-col flex flex-col rounded-[12px] p-[12px]"
          style={{ backgroundColor: "#E5EAF7" }}
        >
          <div className="bfl-sip-calc-card-left flex min-h-0 max-w-full flex-1 flex-col overflow-hidden rounded-[12px] bg-white">
            {renderInvestmentTypeChips()}
            {renderSliders(false)}
          </div>
        </div>

        {/* Right card - Results */}
        <div className="bfl-sip-calc-desktop-col flex max-w-full min-w-0 flex-col">
          <div
            className="flex h-full min-h-0 flex-col gap-[8px] rounded-[12px] p-[8px]"
            style={{ backgroundColor: "#E5EAF7" }}
          >
            <div
              className="flex h-full min-h-0 min-w-0 flex-col rounded-[12px] px-[20px] py-[20px] text-white"
              style={{ backgroundColor: "#5164C8" }}
            >
              {renderSummaryValues(false)}

              <div className="text-black-normal flex min-h-0 flex-1 flex-col gap-[16px] overflow-hidden rounded-[12px] bg-white p-[12px] md:p-[20px]">
                {renderResultTabs()}
                <div className="min-h-0 flex-1 overflow-auto">
                  {activeResultView === "chart" ? renderChartContent() : renderTableContent()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

SipCalculator.displayName = "SipCalculator";
